import { useCallback, useEffect, useMemo, useState } from "react";
import type { EChartsOption } from "echarts";
import { apiFetch } from "../lib/api.js";
import { Chart } from "./Chart.js";
import { ErrorState, LoadingState } from "./States.js";

export type AiUsageDashboardProps = {
  usagePath: string;
  logPath: string;
  /** When true, omit outer title/description (parent provides layout). */
  embedded?: boolean;
  /** When true, omit the dashboard heading (use with PageLayout title). */
  hideIntro?: boolean;
};

type SummaryAgg = {
  total_calls: number;
  total_prompt_tokens: number;
  total_completion_tokens: number;
  total_tokens: number;
  total_cache_read_tokens: number;
  total_cache_creation_tokens: number;
  estimated_cost_usd: number;
  error_count: number;
  avg_latency_ms: number;
  /** ADR-0207: calls made on the workspace's own AI provider (tenant usage only). */
  tenant_route_calls?: number;
};

type BreakdownRow = {
  group_key: string;
  calls: number;
  total_tokens: number;
  estimated_cost_usd: number;
};

type LimitsPayload = {
  ai_calls_per_hour_limit: number | null;
  ai_monthly_token_limit: number | null;
  time_zone: string;
  successful_calls_last_60_min: number;
  current_month_tokens: number;
};

type UsagePayload = {
  summary: SummaryAgg;
  breakdown: BreakdownRow[];
  limits: LimitsPayload;
};

type LogEntry = {
  id: string;
  created_at: string;
  account_email: string | null;
  context_type: string;
  context_name: string | null;
  model_used: string;
  total_tokens: number;
  cache_read_tokens: number;
  status: string;
  error_code: string | null;
};

// ADR-0112: fraction of input tokens served from cache, as a whole-number %.
function fmtCacheHitRate(read: number, write: number, prompt: number): string {
  const denom = prompt + read + write;
  return denom > 0 ? `${Math.round((read / denom) * 100)}%` : "—";
}

function toYmd(d: Date): string {
  return d.toISOString().slice(0, 10);
}

export function rangeLastDays(n: number): { from: string; to: string } {
  const to = new Date();
  const from = new Date(to.getTime() - n * 86_400_000);
  return { from: toYmd(from), to: toYmd(to) };
}

function rangeMonthToDate(): { from: string; to: string } {
  const to = new Date();
  const from = new Date(to.getFullYear(), to.getMonth(), 1);
  return { from: toYmd(from), to: toYmd(to) };
}

function fmtUsd(n: number): string {
  if (!Number.isFinite(n)) return "—";
  return n < 0.01 && n > 0 ? `$${n.toFixed(4)}` : `$${n.toFixed(2)}`;
}

export function AiUsageDashboard(props: AiUsageDashboardProps) {
  const { usagePath, logPath, embedded, hideIntro } = props;

  const [preset, setPreset] = useState<"7d" | "30d" | "90d" | "mtd" | "custom">("30d");
  const [from, setFrom] = useState(rangeLastDays(30).from);
  const [to, setTo] = useState(rangeLastDays(30).to);

  const [usageLoading, setUsageLoading] = useState(true);
  const [usageError, setUsageError] = useState<string | null>(null);
  const [summary, setSummary] = useState<SummaryAgg | null>(null);
  const [limits, setLimits] = useState<LimitsPayload | null>(null);
  const [byDay, setByDay] = useState<BreakdownRow[]>([]);
  const [byContext, setByContext] = useState<BreakdownRow[]>([]);

  const [logLoading, setLogLoading] = useState(true);
  const [logError, setLogError] = useState<string | null>(null);
  const [logEntries, setLogEntries] = useState<LogEntry[]>([]);
  const [logTotal, setLogTotal] = useState(0);
  const [logOffset, setLogOffset] = useState(0);
  const pageSize = 25;

  const applyPreset = useCallback((p: typeof preset) => {
    setPreset(p);
    if (p === "7d") {
      const r = rangeLastDays(7);
      setFrom(r.from);
      setTo(r.to);
    } else if (p === "30d") {
      const r = rangeLastDays(30);
      setFrom(r.from);
      setTo(r.to);
    } else if (p === "90d") {
      const r = rangeLastDays(90);
      setFrom(r.from);
      setTo(r.to);
    } else if (p === "mtd") {
      const r = rangeMonthToDate();
      setFrom(r.from);
      setTo(r.to);
    }
  }, []);

  const loadUsage = useCallback(async () => {
    setUsageLoading(true);
    setUsageError(null);
    try {
      const qDay = new URLSearchParams({ from, to, group_by: "day" });
      const qCtx = new URLSearchParams({ from, to, group_by: "context_type" });
      const [dayRes, ctxRes] = await Promise.all([
        apiFetch<UsagePayload>(`${usagePath}?${qDay.toString()}`),
        apiFetch<UsagePayload>(`${usagePath}?${qCtx.toString()}`)
      ]);
      setSummary(dayRes.summary);
      setLimits(dayRes.limits);
      setByDay(dayRes.breakdown);
      setByContext(ctxRes.breakdown);
    } catch (e) {
      setUsageError(e instanceof Error ? e.message : String(e));
      setSummary(null);
      setLimits(null);
      setByDay([]);
      setByContext([]);
    } finally {
      setUsageLoading(false);
    }
  }, [from, to, usagePath]);

  const loadLog = useCallback(async () => {
    setLogLoading(true);
    setLogError(null);
    try {
      const q = new URLSearchParams({
        from,
        to,
        limit: String(pageSize),
        offset: String(logOffset)
      });
      const data = await apiFetch<{ entries: LogEntry[]; total_count: number }>(`${logPath}?${q.toString()}`);
      setLogEntries(data.entries);
      setLogTotal(data.total_count);
    } catch (e) {
      setLogError(e instanceof Error ? e.message : String(e));
      setLogEntries([]);
      setLogTotal(0);
    } finally {
      setLogLoading(false);
    }
  }, [from, to, logPath, logOffset]);

  useEffect(() => {
    void loadUsage();
  }, [loadUsage]);

  useEffect(() => {
    void loadLog();
  }, [loadLog]);

  const dayChartOption = useMemo((): EChartsOption => {
    const rows = [...byDay].sort((a, b) => a.group_key.localeCompare(b.group_key));
    return {
      grid: { left: 48, right: 16, top: 24, bottom: 48 },
      tooltip: { trigger: "axis" },
      xAxis: { type: "category", data: rows.map((r) => r.group_key), axisLabel: { rotate: rows.length > 14 ? 45 : 0 } },
      yAxis: { type: "value", name: "Calls" },
      series: [{ type: "bar", name: "Calls", data: rows.map((r) => r.calls), itemStyle: { color: "#6366f1" } }]
    };
  }, [byDay]);

  const contextChartOption = useMemo((): EChartsOption => {
    const rows = [...byContext].sort((a, b) => b.calls - a.calls);
    return {
      grid: { left: 120, right: 24, top: 16, bottom: 24 },
      tooltip: { trigger: "axis" },
      xAxis: { type: "value", name: "Calls" },
      yAxis: { type: "category", data: rows.map((r) => r.group_key || "(empty)"), inverse: true },
      series: [{ type: "bar", name: "Calls", data: rows.map((r) => r.calls), itemStyle: { color: "#0ea5e9" } }]
    };
  }, [byContext]);

  const chartHeight = embedded ? 220 : 280;

  return (
    <div style={{ display: "grid", gap: embedded ? 12 : 16 }}>
      {!embedded && !hideIntro ? (
        <div>
          <div style={{ fontWeight: 700, fontSize: 18, marginBottom: 4 }}>AI usage</div>
          <div className="muted">
            OpenAI calls, tokens, and limits for the selected period. The recent log ‘When’ column uses the tenant workspace time zone.
          </div>
        </div>
      ) : null}

      <div className="card" style={{ padding: 12 }}>
        <div className="toolbar" style={{ flexWrap: "wrap", gap: 8, marginBottom: 8 }}>
          <span className="muted" style={{ marginRight: 8 }}>
            Period
          </span>
          {(
            [
              ["7d", "7 days"],
              ["30d", "30 days"],
              ["90d", "90 days"],
              ["mtd", "Month to date"]
            ] as const
          ).map(([key, label]) => (
            <button
              key={key}
              type="button"
              className={`btn btnSm${preset === key ? " btnPrimary" : ""}`}
              onClick={() => applyPreset(key)}
            >
              {label}
            </button>
          ))}
          <button
            type="button"
            className={`btn btnSm${preset === "custom" ? " btnPrimary" : ""}`}
            onClick={() => setPreset("custom")}
          >
            Custom
          </button>
        </div>
        {preset === "custom" ? (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "flex-end" }}>
            <label className="field" style={{ marginBottom: 0 }}>
              <span className="label">From</span>
              <input className="input" type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
            </label>
            <label className="field" style={{ marginBottom: 0 }}>
              <span className="label">To</span>
              <input className="input" type="date" value={to} onChange={(e) => setTo(e.target.value)} />
            </label>
          </div>
        ) : null}
      </div>

      {usageError ? <ErrorState detail={usageError} /> : null}
      {usageLoading ? <LoadingState /> : null}

      {!usageLoading && !usageError && summary && limits ? (
        <>
          {limits ? (
            <div
              className="card"
              style={{
                padding: 12,
                background: "var(--panel-2, rgba(99,102,241,0.08))",
                border: "1px solid var(--border-subtle, rgba(99,102,241,0.25))"
              }}
            >
              <div style={{ fontWeight: 600, marginBottom: 8 }}>Limits (live)</div>
              <div style={{ display: "grid", gap: 6, fontSize: 14 }}>
                <div>
                  <span className="muted">Rolling successful calls (60 min): </span>
                  <b>
                    {limits.successful_calls_last_60_min}
                    {limits.ai_calls_per_hour_limit != null ? ` / ${limits.ai_calls_per_hour_limit} per hour cap` : " (no hourly cap)"}
                  </b>
                </div>
                <div>
                  <span className="muted">Tokens this calendar month ({limits.time_zone}): </span>
                  <b>
                    {limits.current_month_tokens.toLocaleString()}
                    {limits.ai_monthly_token_limit != null
                      ? ` / ${limits.ai_monthly_token_limit.toLocaleString()} cap`
                      : " (no monthly token cap)"}
                  </b>
                </div>
              </div>
            </div>
          ) : null}

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
              gap: 12
            }}
          >
            <div className="card" style={{ padding: 12 }}>
              <div className="muted" style={{ fontSize: 12 }}>
                Total calls
              </div>
              <div style={{ fontSize: 22, fontWeight: 700 }}>{summary.total_calls.toLocaleString()}</div>
            </div>
            <div className="card" style={{ padding: 12 }}>
              <div className="muted" style={{ fontSize: 12 }}>
                Total tokens
              </div>
              <div style={{ fontSize: 22, fontWeight: 700 }}>{summary.total_tokens.toLocaleString()}</div>
            </div>
            <div className="card" style={{ padding: 12 }}>
              <div className="muted" style={{ fontSize: 12 }}>
                Est. cost (USD)
              </div>
              <div style={{ fontSize: 22, fontWeight: 700 }}>{fmtUsd(summary.estimated_cost_usd)}</div>
              {(summary.tenant_route_calls ?? 0) > 0 ? (
                <div className="muted" style={{ fontSize: 12 }}>
                  Estimated at list price — your provider&rsquo;s invoice is authoritative.
                </div>
              ) : null}
            </div>
            <div className="card" style={{ padding: 12 }}>
              <div className="muted" style={{ fontSize: 12 }}>
                Cache hit rate
              </div>
              <div style={{ fontSize: 22, fontWeight: 700 }}>
                {fmtCacheHitRate(
                  summary.total_cache_read_tokens,
                  summary.total_cache_creation_tokens,
                  summary.total_prompt_tokens
                )}
              </div>
              <div className="muted" style={{ fontSize: 11 }}>
                {summary.total_cache_read_tokens.toLocaleString()} read
              </div>
            </div>
            <div className="card" style={{ padding: 12 }}>
              <div className="muted" style={{ fontSize: 12 }}>
                Errors
              </div>
              <div style={{ fontSize: 22, fontWeight: 700 }}>{summary.error_count.toLocaleString()}</div>
            </div>
            <div className="card" style={{ padding: 12 }}>
              <div className="muted" style={{ fontSize: 12 }}>
                Avg latency
              </div>
              <div style={{ fontSize: 22, fontWeight: 700 }}>
                {summary.avg_latency_ms > 0 ? `${Math.round(summary.avg_latency_ms)} ms` : "—"}
              </div>
            </div>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: embedded ? "1fr" : "minmax(0, 1.2fr) minmax(0, 1fr)",
              gap: 12
            }}
          >
            <div className="card" style={{ padding: 12 }}>
              <div style={{ fontWeight: 600, marginBottom: 8 }}>Calls by day</div>
              {byDay.length === 0 ? (
                <div className="muted">No calls in this range.</div>
              ) : (
                <Chart option={dayChartOption} height={chartHeight} />
              )}
            </div>
            <div className="card" style={{ padding: 12 }}>
              <div style={{ fontWeight: 600, marginBottom: 8 }}>Calls by context type</div>
              {byContext.length === 0 ? (
                <div className="muted">No breakdown for this range.</div>
              ) : (
                <Chart option={contextChartOption} height={chartHeight} />
              )}
            </div>
          </div>
        </>
      ) : null}

      <div className="card" style={{ padding: 12 }}>
        <div style={{ fontWeight: 600, marginBottom: 8 }}>Recent log (paged)</div>
        {logError ? <ErrorState detail={logError} /> : null}
        {logLoading ? <LoadingState /> : null}
        {!logLoading && !logError ? (
          <>
            <div style={{ overflowX: "auto" }}>
              <table className="table" style={{ width: "100%", fontSize: 13 }}>
                <thead>
                  <tr>
                    <th>When</th>
                    <th>User</th>
                    <th>Context</th>
                    <th>Name</th>
                    <th>Model</th>
                    <th>Tokens</th>
                    <th>Cache</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {logEntries.map((row) => (
                    <tr key={row.id}>
                      <td className="mono" style={{ whiteSpace: "nowrap" }}>
                        {row.created_at}
                      </td>
                      <td>{row.account_email ?? "—"}</td>
                      <td className="mono">{row.context_type}</td>
                      <td>{row.context_name ?? "—"}</td>
                      <td className="mono">{row.model_used}</td>
                      <td>{row.total_tokens.toLocaleString()}</td>
                      <td>{row.cache_read_tokens.toLocaleString()}</td>
                      <td>
                        <span className={row.status === "success" ? "pill pillOk" : "pill pillBad"}>{row.status}</span>
                        {row.error_code ? (
                          <span className="muted" style={{ marginLeft: 6 }}>
                            {row.error_code}
                          </span>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {logEntries.length === 0 ? <div className="muted">No rows in this period.</div> : null}
            <div className="toolbar" style={{ marginTop: 12 }}>
              <button
                type="button"
                className="btn btnSm"
                disabled={logOffset <= 0}
                onClick={() => setLogOffset((o) => Math.max(0, o - pageSize))}
              >
                Previous
              </button>
              <button
                type="button"
                className="btn btnSm"
                disabled={logOffset + pageSize >= logTotal}
                onClick={() => setLogOffset((o) => o + pageSize)}
              >
                Next
              </button>
              <span className="muted" style={{ marginLeft: 8 }}>
                {logTotal === 0 ? "0 rows" : `${logOffset + 1}–${Math.min(logOffset + logEntries.length, logTotal)} of ${logTotal}`}
              </span>
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
}
